import { getSessionReplayProperties, init, setSessionId, shutdown } from '@amplitude/session-replay-browser';
import * as browserHelper from 'usage-tracker-core/client/browserHelper';
import { debounce } from 'usage-tracker-core/common/helpers';
import * as srConfig from './config';
import * as srConstants from './constants';
import * as srUtils from './utils';
const withSessionReplay = (tracker, options = {}) => {
  const {
    // The AbortSignal to be used for shutting down the Session Replay instance
    abortSignal,
    // The  timeout for Session Replay to be shutdown automatically due to `usage-tracker-js` inactivity
    inactivityTimeout = srConstants.DEFAULT_SHUTDOWN_TIMEOUT
  } = options;
  if (browserHelper.isDebugEnabled) {
    console.warn('[usage-tracker-session-replay] debug mode enabled.');
  }
  const platformTracker = tracker.clone(srConstants.PLATFORM_TRACKER_CONFIG);

  // This holds the current Promise, to prevent `.track()` from instantiating multiple promises
  // while one is still being resolved. It also ensures that we only have only Promise active
  // whilst Session Replay is bootstrapping or bootstrapped.
  let sessionReplayPromise;
  const getSessionReplayPromise = async (deviceId, sessionId) => {
    // Generates the Session Replay Config based on the provided options
    // Allows the config to be updated on-demand based on reinitialization
    const sessionReplayConfig = srConfig.getSessionReplayConfig(options, deviceId, sessionId);
    if (browserHelper.isDebugEnabled) {
      console.debug('[usage-tracker-session-replay] initialising.', {
        sessionReplayConfig
      });
    }

    // Creates the Session Replay instance. Note that this returns an object
    // with a Promise which is the actual SessionReplay instance
    const sessionReplayInitResult = init(
    // Passed the Deployable Name, Deployable Version and Portal ID
    // as the "Api Key" for Session Replay as our BEs forward the actual API Key
    srUtils.getAuthorizationKey(), sessionReplayConfig);

    // Awaits for the Session Replay instance to be bootstrapped
    await sessionReplayInitResult.promise;

    // @TODO: remove sampleRate check once we remove sampleRate from the codebase
    if (!options.optOut && options.sampleRate !== 0) {
      // @ts-expect-error - clone with a generic base config prevents TypeScript from resolving the standalone track signature
      platformTracker.track('Platform', 'Replay Recorded');
    }
  };

  // This method is used to shutdown the Session Replay instance
  // and signal that a new bootstrap must be called before using Session Replay again
  const shutdownSessionReplay = (alllowReinitialisation = true) => {
    var _sessionReplayPromise;
    if (browserHelper.isDebugEnabled) {
      console.debug('[usage-tracker-session-replay] shutting down.');
    }

    // If the AbortSignal is aborted, we shutdown the Session Replay instance
    // to prevent any further events from being sent to Amplitude
    shutdown();

    // Rests the `session replay` promise to `undefined` to allow for a new Session Replay instance
    // to be bootstrapped as soon as `.track` is called again
    sessionReplayPromise = alllowReinitialisation ? undefined : // This handles the scenario, where if the `.abort()` is called before the Session Replay
    // instance was created, the Promise does not exist yet, meaning a `.track()` call would
    // actually trigger the Session Replay to be bootstrapped, which is not what we want.
    (_sessionReplayPromise = sessionReplayPromise) !== null && _sessionReplayPromise !== void 0 ? _sessionReplayPromise : Promise.resolve();
  };

  // Debounces the `shutdownSessionReplay` method to be called after the `inactivityTimeout`
  const inactivityShutdownDebouncer = debounce(shutdownSessionReplay, inactivityTimeout);

  // Updates the Session Replay Properties to the latest deviceId and sessionId
  const updateSessionReplayProperties = (deviceId, sessionId) => {
    var _sessionReplayPromise2;
    // Ensures that only one Promise is active at a time, preventing concurrent/parallel/async
    // resolutions of the initialisation and environment check of Session Replay
    sessionReplayPromise = (_sessionReplayPromise2 = sessionReplayPromise) !== null && _sessionReplayPromise2 !== void 0 ? _sessionReplayPromise2 : getSessionReplayPromise(deviceId, sessionId);

    // Note: as of @amplitude/session-replay-browser@1.29.0, init().promise can reject in
    // test environments where Jasmine's console invariant intercepts the SDK's console.error
    // call (upgraded from console.warn in that release) and throws synchronously, propagating
    // the rejection. We attach the catch directly to sessionReplayPromise so it does not run
    // through the success callbacks, and reset so the next .track() call can retry.
    void sessionReplayPromise.catch(() => {
      sessionReplayPromise = undefined;
    });
    void sessionReplayPromise.then(() => {
      // Initiates (and resets) the shutdown debouncer, every time a `.track()` is called
      inactivityShutdownDebouncer();

      // Retrieves the current Session Replay Properties stored on Amplitude's SDK
      const sessionReplayProperties = getSessionReplayProperties();

      // Retrieves the latest deviceId and sessionId from Amplitude since they don't expose
      // an API to get the current deviceId and sessionId
      const [ampDeviceId, ampSessionId] = srUtils.extractAmplitudeIdentifiers(sessionReplayProperties);

      // An `usage-tracker` session might change during time (device id's could also change)
      // hence if the sessionId differs, we update the Session Replay instance with the new
      // session id's and device id's so that the events get properly attached
      // Note: Calling `setSessionId` will trigger a call to Amplitude's /config endpoint
      // for Session Replay -- not sure why this is done, but it's how the SDK works
      if (sessionId !== ampSessionId || deviceId !== ampDeviceId) {
        setSessionId(sessionId, deviceId);
      }
    });
  };

  // Creates a Proxy for an actual `usage-tracker` instance that will inject Amplitude's Session Replay
  // and do all necessary logic to ensure events are properly configured and tracked
  const applyProxy = trackerInstance => {
    return new Proxy(trackerInstance, {
      get: (target, property) => {
        // Wraps the native track method (for non-standalone tracking only at the moment)
        // that attaches Session Replay Properties
        if (property === 'track') {
          return (...trackArgs) => {
            // Determines if the tracker is in standalone mode or not
            const isStandaloneMode = srUtils.isTrackerInStandaloneMode(target);

            // Retrieves the Event Properties from the `.track()` call
            const trackProperties = isStandaloneMode ? trackArgs[2] : trackArgs[1];

            // Read identity (non-mutating) before dispatching, so the advance in
            // `target.track()` is the single source of truth for the counters and
            // any first-use default is minted synchronously here.
            const [deviceId, sessionId] = srUtils.getTrackerMetaProperties(target, trackProperties === null || trackProperties === void 0 ? void 0 : trackProperties.deviceId);

            // Note that the Session Replay Properties might get "flagged" as unknown properties
            // @TODO: Once we migrate away from `events.yaml` we can simplify the whole method call.
            // @ts-expect-error - we spread the arguments as is, so we don't need to type it
            target.track(...trackArgs);

            // Updates the Session Replay Properties to the latest deviceId and sessionId
            updateSessionReplayProperties(deviceId, sessionId);
          };
        }

        // Ensures that when cloning a tracker instance, Session Replay
        // is still attached to the new cloned instance
        if (property === 'clone') {
          return cloneConfig => {
            return applyProxy(target.clone(cloneConfig));
          };
        }

        // Fallbacks to default behaviour for all other operations
        return target[property];
      }
    });
  };

  // Allows custom AbortSignal to be passed for shutting down the Session Replay instance
  abortSignal === null || abortSignal === void 0 || abortSignal.addEventListener('abort', () => {
    // Manual shutdowns prevent Session Replay from re-bootstrapping
    shutdownSessionReplay(false);
  });

  // Applies a Proxy to the current Usage Tracker instance
  return applyProxy(tracker);
};
export { withSessionReplay };